import { Brand } from "@tento-ui/brand/brand";
import { MarketingMobileMenu } from "@tento-ui/brand/header/marketing-mobile-menu";
import { MarketingHeader } from "@tento-ui/brand/header/marketing-header";
import { LandingFooter } from "@tento-ui/brand/landing/landing-footer";
import type {
  Preset,
  RendererOverrides,
  SectionSet,
  TokenSet,
} from "@tento-ui/brand/page-engine.types";
import type { ThemeRef } from "@tento-ui/brand/page-model";
import { BrandTokens } from "@tento-ui/brand/tokens/index";
import type { Style } from "@tento-ui/component-styles";
import { Breadcrumb } from "@tento-ui/components/breadcrumb/breadcrumb";
import { NavCenteredPopoverRow } from "@tento-ui/brand/blog/nav-centered-popover-row";

import { MarketingFooter } from "../marketing/lona-marketing-footer";
import {
  defaultSections,
  landingPageStyle,
  makePageFactory,
} from "./landing-content";
import {
  landingComparisonRenderer,
  landingCtaRenderer,
  landingFaqRenderer,
  landingFeatureGridRenderer,
  landingHeroRenderer,
  landingIntegrationsRenderer,
  landingPricingRenderer,
  landingSocialProofRenderer,
} from "./landing-page-engine";
import type { PageEnginePresetConfig } from "./page-engine-presets";
import type { PageEngineNodeProps } from "./to-props";
import { stickerSheetRenderer } from "./sticker-section";

export type PublicLandingConfig = {
  brandDS: Brand.DesignSystem;
  landing: Brand.LandingPage["landing"];
};

const landingChrome: Brand.ChromeVisitor = {
  makeLayout(parts): HTMLElement {
    const $layout = document.createElement("div");
    $layout.assignStyles({
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      width: "100%",
    });

    parts.header.appendChild(parts.breadcrumb);
    parts.footer.assignStyles({ order: "2" });
    $layout.append(parts.header, parts.footer);
    return $layout;
  },
};

const defaultNav: Brand.DesignSystem["nav"] = {
  header: {
    make(nav) {
      const $header = MarketingHeader.make();
      $header.appendChild(NavCenteredPopoverRow.make().bind(nav, $header));
      $header.appendChild(MarketingMobileMenu.makeWith(nav));
      return $header;
    },
    bind($header) {
      return $header;
    },
  },
  breadcrumb: {
    make() {
      return Breadcrumb.makeWith();
    },
    bind($el) {
      return $el;
    },
  },
  footer: {
    make() {
      const $footer = LandingFooter.make();
      $footer.appendChild(MarketingFooter.make());
      return $footer;
    },
    bind($el) {
      return $el;
    },
  },
};

function makeBrandDS(opts: {
  bg: Option<string>;
  fonts: string[];
  colors: Style.Compat;
  typography: Style.Compat;
  spacing: Style.Compat;
  contentTypography: Option<Style.Compat>;
  nav: Option<Brand.DesignSystem["nav"]>;
}): Brand.DesignSystem {
  return {
    bg: opts.bg,
    fonts: opts.fonts,
    colors: opts.colors,
    typography: opts.typography,
    contentTypography: opts.contentTypography,
    spacing: opts.spacing,
    chrome: BrandTokens.chrome,
    nav: opts.nav ?? defaultNav,
    layout: landingChrome,
  };
}

export const publicLanding: Brand.LandingPage["landing"] = {
  style: landingPageStyle,
  sections: defaultSections,
  page: makePageFactory(defaultSections),
};

export const publicLandingConfig: PublicLandingConfig = {
  brandDS: makeBrandDS({
    bg: "#f7f3eb",
    fonts: BrandTokens.Typography.loafFonts,
    colors: BrandTokens.Color.loaf,
    typography: BrandTokens.Typography.loaf,
    spacing: BrandTokens.Spacing.loaf,
    contentTypography: null,
    nav: null,
  }),
  landing: publicLanding,
};

const landingSectionSet: SectionSet<PageEngineNodeProps> = {
  chrome: landingChrome,
  sections: {
    hero: landingHeroRenderer,
    "feature-grid": landingFeatureGridRenderer,
    "social-proof": landingSocialProofRenderer,
    integrations: landingIntegrationsRenderer,
    comparison: landingComparisonRenderer,
    pricing: landingPricingRenderer,
    faq: landingFaqRenderer,
    cta: landingCtaRenderer,
  },
};

const landingOverrides: RendererOverrides<PageEngineNodeProps> = {
  byId: { "landing.stickers": stickerSheetRenderer },
  byKind: null,
};

const landingPreset: Preset<PageEngineNodeProps> = {
  name: "lona-marketing",
  theme: {
    tokens: "lona-marketing",
    variant: "lona-marketing",
  },
  overrides: landingOverrides,
};

const loafPreset: Preset<PageEngineNodeProps> = {
  name: "loaf",
  theme: {
    tokens: "loaf",
    variant: "loaf",
  },
  overrides: landingOverrides,
};

const landingTokens = publicLandingTokenSet();

export function resolvePublicLandingPageEnginePreset(
  theme: ThemeRef,
): PageEnginePresetConfig {
  const name = resolvePublicLandingPreset(theme);
  return {
    preset: name === "loaf" ? loafPreset : landingPreset,
    sectionSet: landingSectionSet,
    tokenSet: landingTokens,
  };
}

// A page model names its theme, and an UNKNOWN name falls back to the
// marketing look. That default is why a mis-registered theme fails the way it
// does: the page renders with the marketing hero renderer, which reads a
// `primaryCta.label` the model never promised, and the whole page dies on a
// TypeError three frames inside a section it never asked for.
function resolvePublicLandingPreset(theme: ThemeRef): "lona-marketing" | "loaf" {
  const name = typeof theme === "string" ? theme : theme.variant;
  if (name === "loaf") return "loaf";
  return "lona-marketing";
}

function publicLandingTokenSet(): TokenSet {
  return Brand.collectLandingTokens({
    ds: publicLandingConfig.brandDS,
    landing: publicLandingConfig.landing,
  });
}
